import { Quat, Vec3 } from 'playcanvas';

import { parseVehicleMotionCsv } from './csv-vehicle-parser';
import { Events } from './events';
import { HelperPoint } from './helper-point';
import type { Vec3Tuple } from './math-types';
import { Scene } from './scene';
import { VehicleMotionController, VehicleMotionTargetSet, VehicleMotionTrackSet, TransformTrackData } from './vehicle-motion-controller';

export type VehicleMotionAxisPreset = 'zUpToYUp' | 'yUpKeep';

export type VehicleMotionEulerOverrides = {
    /** Negate CSV pitch angle before conversion. */
    invertPitch?: boolean;

    /** Negate CSV roll angle before conversion. */
    invertRoll?: boolean;

    /** Negate CSV yaw angle before conversion. */
    invertYaw?: boolean;

    /** Swap CSV pitch and roll inputs. */
    swapPitchRoll?: boolean;
};

export type VehicleMotionImportOptions = {
    axisPreset?: VehicleMotionAxisPreset;
    eulerOverrides?: VehicleMotionEulerOverrides;

    /**
     * How to interpret TYRE_PHI{N} values.
     * - 'deg' (default): values are degrees.
     * - 'rad': values are radians.
     */
    wheelSpinUnits?: 'deg' | 'rad';

    /**
     * Which local axis to spin wheels around.
     * Defaults to 'x' (PlayCanvas RIGHT).
     *
     * Many wheel meshes use X as the axle axis, but some assets are authored
     * with Z (or Y) as the axle; this option allows correcting that.
     */
    wheelSpinAxis?: 'x' | 'y' | 'z';

    /**
     * When true, uses TYRE_SA{N} as a steering angle (degrees) around local up.
     * Defaults to false because slip angle is not a steering angle in most datasets
     * and can easily produce sideways wheels.
     */
    useSlipAngleAsSteer?: boolean;

    /**
     * Frames-per-second used to convert CSV seconds into timeline frames.
     * Defaults to current timeline frameRate or 30.
     */
    fps?: number;

    /**
     * When true, the timeline length is extended to fit the imported motion.
     * Defaults to true.
     */
    extendTimeline?: boolean;
};

export type VehicleMotionImportResult = {
    root: HelperPoint;
    body: HelperPoint;
    wheels: [HelperPoint, HelperPoint, HelperPoint, HelperPoint];
    controller: VehicleMotionController;
    warnings: string[];
    durationSec: number;
    framesRequired: number;
    fps: number;
};

const mapPosition = (p: Vec3Tuple, preset: VehicleMotionAxisPreset): Vec3Tuple => {
    // Default mapping assumes CSV is right-handed with +Z up and +Y forward.
    // PlayCanvas is right-handed with +Y up and +Z forward.
    // A proper rotation (det=+1) that performs Z-up -> Y-up is RotX(-90deg):
    // (x, y, z)_csv -> (x, z, -y)_pc
    if (preset === 'zUpToYUp') {
        return [p[0], p[2], -p[1]];
    }

    return [p[0], p[1], p[2]];
};

type WheelRole = 'FL' | 'FR' | 'RL' | 'RR';

const inferWheelRolesFromFirstSample = (
    s0: ReturnType<typeof parseVehicleMotionCsv>['samples'][0],
    axisPreset: VehicleMotionAxisPreset,
    rootPos: Vec3,
    rootRotInv: Quat
): (WheelRole | null)[] | null => {
    // Require all 4 wheel centers to avoid misleading labels.
    for (let i = 0; i < 4; i++) {
        if (!s0.tyres[i]?.wheelCenter) return null;
    }

    const vWorld = new Vec3();
    const vLocal = new Vec3();

    const pts = [0, 1, 2, 3].map((i) => {
        const wc = s0.tyres[i].wheelCenter as Vec3Tuple;
        const wcT = mapPosition(wc, axisPreset);
        vWorld.set(wcT[0], wcT[1], wcT[2]).sub(rootPos);
        rootRotInv.transformVector(vWorld, vLocal);
        return { i, x: vLocal.x, z: vLocal.z };
    });

    // Front is -Z in PlayCanvas local space (Vec3.FORWARD is (0, 0, -1)).
    // Root-local is aligned to bodyRot0, so we can classify front/rear by Z.
    const byZ = [...pts].sort((a, b) => a.z - b.z);
    const front = byZ.slice(0, 2);
    const rear = byZ.slice(2, 4);
    if (front.length !== 2 || rear.length !== 2) return null;

    const frontByX = [...front].sort((a, b) => a.x - b.x); // left first (smaller X)
    const rearByX = [...rear].sort((a, b) => a.x - b.x);

    const roles: (WheelRole | null)[] = [null, null, null, null];
    roles[frontByX[0].i] = 'FL';
    roles[frontByX[1].i] = 'FR';
    roles[rearByX[0].i] = 'RL';
    roles[rearByX[1].i] = 'RR';

    // Defensive: ensure all assigned.
    return roles.every(r => r) ? roles : null;
};

const getBasisQuat = (preset: VehicleMotionAxisPreset) => {
    const q = new Quat();
    if (preset === 'zUpToYUp') {
        q.setFromAxisAngle(Vec3.RIGHT, -90);
    }
    return q;
};

const getBodyQuat = (pitchDeg: number | null, rollDeg: number | null, yawDeg: number | null, preset: VehicleMotionAxisPreset, overrides: VehicleMotionEulerOverrides) => {
    const pitch0 = pitchDeg ?? 0;
    const roll0 = rollDeg ?? 0;
    const yaw0 = yawDeg ?? 0;

    let pitch = overrides.swapPitchRoll ? roll0 : pitch0;
    let roll = overrides.swapPitchRoll ? pitch0 : roll0;
    const yaw = overrides.invertYaw ? -yaw0 : yaw0;

    // Apply sign overrides after any swap so the toggles map to the final pitch/roll values.
    if (overrides.invertPitch) pitch = -pitch;
    if (overrides.invertRoll) roll = -roll;

    // Interpret CSV angles as intrinsic XYZ Euler angles in *CSV axis space*, then
    // convert into PlayCanvas axis space using a basis-change quaternion.
    const qCsv = new Quat().setFromEulerAngles(pitch, roll, yaw);

    const qBasis = getBasisQuat(preset);
    const qBasisInv = qBasis.clone().invert();

    // qPc = qBasis * qCsv * qBasis^-1
    const qTmp = new Quat().mul2(qBasis, qCsv);
    return qTmp.mul(qBasisInv).normalize();
};

const degFromUnits = (value: number, units: 'deg' | 'rad') => {
    if (units === 'rad') return value * (180 / Math.PI);
    return value;
};

const getAxisVec = (axis: 'x' | 'y' | 'z') => {
    // PlayCanvas uses -Z as forward, but for axis-angle the sign only affects spin direction.
    if (axis === 'x') return Vec3.RIGHT;
    if (axis === 'y') return Vec3.UP;
    return Vec3.FORWARD;
};

const toTrack = (): TransformTrackData => ({
    frames: [],
    positions: [],
    rotations: []
});

const pushKey = (track: TransformTrackData, frame: number, pos: Vec3, rot: Quat) => {
    const prev = track.frames[track.frames.length - 1];
    if (prev !== undefined && frame <= prev) {
        return false;
    }

    track.frames.push(frame);
    track.positions.push(pos.x, pos.y, pos.z);
    track.rotations.push(rot.x, rot.y, rot.z, rot.w);
    return true;
};

const ensureAtLeastOneKey = (track: TransformTrackData) => {
    if (track.frames.length > 0) return;
    track.frames.push(0);
    track.positions.push(0, 0, 0);
    track.rotations.push(0, 0, 0, 1);
};

/**
 * Import vehicle motion CSV contents and create helper hierarchy + controller.
 *
 * Current behavior is intentionally minimal:
 * - Creates `v{N}_root` (non-animated) and 5 animated helpers (body + 4 wheels).
 * - Builds local-space transform tracks relative to the root's initial pose.
 * - Computes wheel orientation using Q_wheel = Q_body * Q_steer * Q_spin.
 */
export async function importVehicleMotionCsvText(
    text: string,
    scene: Scene,
    events: Events,
    options: VehicleMotionImportOptions = {}
): Promise<VehicleMotionImportResult | null> {
    const warnings: string[] = [];

    const axisPreset = options.axisPreset ?? 'zUpToYUp';
    const overrides = options.eulerOverrides ?? {};
    const wheelSpinUnits = options.wheelSpinUnits ?? 'deg';
    const wheelSpinAxis = options.wheelSpinAxis ?? 'x';
    const useSlipAngleAsSteer = options.useSlipAngleAsSteer ?? false;

    const timelineFps = events.invoke('timeline.frameRate');
    let fps: number;
    if (typeof options.fps === 'number' && Number.isFinite(options.fps) && options.fps > 0) {
        fps = options.fps;
    } else if (typeof timelineFps === 'number' && Number.isFinite(timelineFps) && timelineFps > 0) {
        fps = timelineFps;
    } else {
        fps = 30;
    }

    const parsed = parseVehicleMotionCsv(text, { zeroTimeToFirstSample: true });
    warnings.push(...parsed.warnings);

    if (parsed.samples.length < 1) {
        warnings.push('CSV contained no usable samples.');
        console.warn(warnings.join('\n'));
        return null;
    }

    const vehicleNo = parsed.meta.vehicleNo;
    const vehicleLabel = (vehicleNo !== null && Number.isFinite(vehicleNo)) ? `v${vehicleNo}` : 'v';

    // Root reference pose: first sample body transform.
    const s0 = parsed.samples[0];
    if (!s0.body.position) {
        warnings.push('First sample is missing body position; cannot establish root.');
        console.warn(warnings.join('\n'));
        return null;
    }

    const bodyPos0T = mapPosition(s0.body.position, axisPreset);
    const bodyRot0 = getBodyQuat(s0.body.anglesDeg.pitch, s0.body.anglesDeg.roll, s0.body.anglesDeg.yaw, axisPreset, overrides);

    const rootPos = new Vec3(bodyPos0T[0], bodyPos0T[1], bodyPos0T[2]);
    const rootRot = bodyRot0.clone();
    const rootRotInv = rootRot.clone().invert();

    const wheelRoles = inferWheelRolesFromFirstSample(s0, axisPreset, rootPos, rootRotInv);

    // Create helper hierarchy.
    // Important: set TRS and logical parenting BEFORE adding to the scene.
    // Scene/UI listeners rebuild lists on elementAdded, so helpers must already have parentElement
    // set to avoid appearing at top-level until the next rebuild.
    const root = new HelperPoint({ name: `${vehicleLabel}_root`, animated: false, visible: true, vehicleId: vehicleNo });
    root.entity.setLocalPosition(rootPos);
    root.entity.setLocalRotation(rootRot);
    await scene.add(root);

    const body = new HelperPoint({ name: `${vehicleLabel}_body`, animated: true, visible: true, parentEntity: root.entity, vehicleId: vehicleNo });
    // Maintain a logical hierarchy for editor UI (indentation / parenting menus).
    body.parentElement = root;
    root.childElements.push(body);
    await scene.add(body);

    const wheels: [HelperPoint, HelperPoint, HelperPoint, HelperPoint] = [
        new HelperPoint({ name: `${vehicleLabel}_wheel1`, animated: true, visible: true, parentEntity: root.entity, vehicleId: vehicleNo, groupId: wheelRoles?.[0] ?? null }),
        new HelperPoint({ name: `${vehicleLabel}_wheel2`, animated: true, visible: true, parentEntity: root.entity, vehicleId: vehicleNo, groupId: wheelRoles?.[1] ?? null }),
        new HelperPoint({ name: `${vehicleLabel}_wheel3`, animated: true, visible: true, parentEntity: root.entity, vehicleId: vehicleNo, groupId: wheelRoles?.[2] ?? null }),
        new HelperPoint({ name: `${vehicleLabel}_wheel4`, animated: true, visible: true, parentEntity: root.entity, vehicleId: vehicleNo, groupId: wheelRoles?.[3] ?? null })
    ];

    for (const w of wheels) {
        w.parentElement = root;
        root.childElements.push(w);
        await scene.add(w);
    }

    // Ensure any hierarchy-dependent UIs can refresh even if they missed intermediate adds.
    events.fire('hierarchy.changed');

    // Build tracks (local to root)
    const bodyTrack = toTrack();
    const wheelTracks: VehicleMotionTrackSet['wheels'] = [toTrack(), toTrack(), toTrack(), toTrack()];

    const vWorld = new Vec3();
    const vLocal = new Vec3();

    const qWheelWorld = new Quat();
    const qSteer = new Quat();
    const qSpin = new Quat();
    const qLocal = new Quat();
    const qBodyPrev = new Quat();
    const qBodyNext = new Quat();
    const qBodyInterp = new Quat();

    const posPrev = new Vec3();
    const posNext = new Vec3();
    const posInterp = new Vec3();

    const UP = Vec3.UP;
    const AXLE = getAxisVec(wheelSpinAxis);

    // Prevent wheel spin loss when TYRE_PHI changes more than 180deg between samples.
    // We subdivide wheel keyframes so each segment is small enough for slerp to represent.
    const MAX_SPIN_STEP_DEG = 120;

    const addWheelKey = (track: TransformTrackData, frame: number, pLocal: Vec3, qWheelWorldIn: Quat) => {
        qLocal.mul2(rootRotInv, qWheelWorldIn);
        if (!pushKey(track, frame, pLocal, qLocal)) {
            return false;
        }
        return true;
    };

    let lastFrame = -Infinity;

    let prevSample: typeof parsed.samples[number] | null = null;
    // Track previous spin (in degrees) per wheel so we can subdivide on large deltas.
    const prevSpinDeg: (number | null)[] = [null, null, null, null];
    const prevSteerDeg: number[] = [0, 0, 0, 0];

    for (const sample of parsed.samples) {
        const frame = sample.tSec * fps;
        if (!(frame > lastFrame)) {
            warnings.push(`Row ${sample.sourceRow}: non-increasing time after resampling (frame=${frame}); skipping sample.`);
            continue;
        }
        lastFrame = frame;

        // body
        if (sample.body.position) {
            const pT = mapPosition(sample.body.position, axisPreset);
            vWorld.set(pT[0], pT[1], pT[2]).sub(rootPos);
            rootRotInv.transformVector(vWorld, vLocal);

            const qBodyWorld = getBodyQuat(sample.body.anglesDeg.pitch, sample.body.anglesDeg.roll, sample.body.anglesDeg.yaw, axisPreset, overrides);
            qLocal.mul2(rootRotInv, qBodyWorld);

            if (!pushKey(bodyTrack, frame, vLocal, qLocal)) {
                warnings.push(`Row ${sample.sourceRow}: could not add body keyframe (non-increasing frame).`);
            }
        } else {
            warnings.push(`Row ${sample.sourceRow}: missing body position; body keyframe skipped.`);
        }

        // wheels
        for (let i = 0; i < 4; i++) {
            const tyre = sample.tyres[i];

            // position
            const wc = tyre.wheelCenter ?? sample.body.position;
            if (!wc) {
                warnings.push(`Row ${sample.sourceRow}: missing wheel${i + 1} center and body position; wheel keyframe skipped.`);
                continue;
            }

            const wcT = mapPosition(wc, axisPreset);
            vWorld.set(wcT[0], wcT[1], wcT[2]).sub(rootPos);
            rootRotInv.transformVector(vWorld, vLocal);

            // orientation
            const qBodyWorld = getBodyQuat(sample.body.anglesDeg.pitch, sample.body.anglesDeg.roll, sample.body.anglesDeg.yaw, axisPreset, overrides);

            // Steering: prefer disabled by default (slip angle is not a steering angle).
            const steerDeg = useSlipAngleAsSteer ? (tyre.slipAngleDeg ?? 0) : 0;

            // Wheel spin: interpret TYRE_PHI as absolute angle (best-effort).
            // Keep continuity by carrying forward previous value when missing.
            const rawPhi = tyre.phiDeg;
            const spinDegHere = (rawPhi !== null) ? degFromUnits(rawPhi, wheelSpinUnits) : (prevSpinDeg[i] ?? 0);

            // Subdivide wheel-only keyframes on large spin deltas so quaternion slerp doesn't
            // collapse multiple revolutions into the shortest arc (or identity if endpoints match).
            const prevFrame = prevSample ? (prevSample.tSec * fps) : null;
            const hasPrev = prevFrame !== null && prevSample !== null;

            if (!hasPrev || prevSpinDeg[i] === null) {
                // First key for this wheel.
                qSteer.setFromAxisAngle(UP, steerDeg);
                qSpin.setFromAxisAngle(AXLE, spinDegHere);
                qWheelWorld.mul2(qBodyWorld, qSteer).mul(qSpin).normalize();

                if (!addWheelKey(wheelTracks[i], frame, vLocal, qWheelWorld)) {
                    warnings.push(`Row ${sample.sourceRow}: could not add wheel${i + 1} keyframe (non-increasing frame).`);
                }
            } else {
                const prev = prevSample;

                // prev wheel local pos
                const prevTyre = prev.tyres[i];
                const prevWc = prevTyre.wheelCenter ?? prev.body.position;
                if (!prevWc || !prev.body.position) {
                    // Can't reliably interpolate; just add a single key at the current sample.
                    qSteer.setFromAxisAngle(UP, steerDeg);
                    qSpin.setFromAxisAngle(AXLE, spinDegHere);
                    qWheelWorld.mul2(qBodyWorld, qSteer).mul(qSpin).normalize();
                    if (!addWheelKey(wheelTracks[i], frame, vLocal, qWheelWorld)) {
                        warnings.push(`Row ${sample.sourceRow}: could not add wheel${i + 1} keyframe (non-increasing frame).`);
                    }
                } else {
                    const prevWcT = mapPosition(prevWc as Vec3Tuple, axisPreset);
                    vWorld.set(prevWcT[0], prevWcT[1], prevWcT[2]).sub(rootPos);
                    rootRotInv.transformVector(vWorld, posPrev);

                    posNext.copy(vLocal);

                    qBodyPrev.copy(getBodyQuat(prev.body.anglesDeg.pitch, prev.body.anglesDeg.roll, prev.body.anglesDeg.yaw, axisPreset, overrides));
                    qBodyNext.copy(qBodyWorld);

                    const steer0 = prevSteerDeg[i] ?? 0;
                    const steer1 = steerDeg;

                    const spin0 = prevSpinDeg[i] ?? 0;
                    const spin1 = spinDegHere;
                    const spinDelta = spin1 - spin0;

                    const segments = Math.max(1, Math.ceil(Math.abs(spinDelta) / MAX_SPIN_STEP_DEG));
                    for (let k = 1; k <= segments; k++) {
                        const t = k / segments;
                        const frameK = (prevFrame as number) + (frame - (prevFrame as number)) * t;

                        posInterp.lerp(posPrev, posNext, t);

                        qBodyInterp.slerp(qBodyPrev, qBodyNext, t);

                        const steerK = steer0 + (steer1 - steer0) * t;
                        const spinK = spin0 + spinDelta * t;

                        qSteer.setFromAxisAngle(UP, steerK);
                        qSpin.setFromAxisAngle(AXLE, spinK);

                        qWheelWorld.mul2(qBodyInterp, qSteer).mul(qSpin).normalize();

                        if (!addWheelKey(wheelTracks[i], frameK, posInterp, qWheelWorld)) {
                            warnings.push(`Row ${sample.sourceRow}: could not add wheel${i + 1} keyframe (non-increasing frame).`);
                            break;
                        }
                    }
                }
            }

            prevSpinDeg[i] = spinDegHere;
            prevSteerDeg[i] = steerDeg;
        }

        prevSample = sample;
    }

    ensureAtLeastOneKey(bodyTrack);
    for (let i = 0; i < 4; i++) ensureAtLeastOneKey(wheelTracks[i]);

    const durationSec = parsed.samples[parsed.samples.length - 1].tSec;
    const framesRequired = Math.max(1, Math.ceil(durationSec * fps) + 1);

    if (options.extendTimeline ?? true) {
        const currentFrames = events.invoke('timeline.frames');
        if (typeof currentFrames === 'number' && Number.isFinite(currentFrames) && framesRequired > currentFrames) {
            events.fire('timeline.setFrames', framesRequired);
        }
    }

    // Scrub timeline to start.
    events.fire('timeline.setFrame', 0);

    // C.7: Auto-set time origin offset from the CSV’s raw time origin.
    // This preserves the original time meaning (e.g., t=0 at collision).
    try {
        const rawTimeOrigin = parsed.meta.timeOffsetSec ?? parsed.samples[0]?.tRawSec;
        if (Number.isFinite(rawTimeOrigin) && rawTimeOrigin !== 0) {
            events.fire('timeline.setTimeOriginOffset', rawTimeOrigin);
        }
    } catch {
        // best effort
    }

    const tracks: VehicleMotionTrackSet = {
        body: bodyTrack,
        wheels: wheelTracks
    };

    const targets: VehicleMotionTargetSet = {
        body: body.entity,
        wheels: [wheels[0].entity, wheels[1].entity, wheels[2].entity, wheels[3].entity]
    };

    const controller = new VehicleMotionController(events, targets, tracks);

    // Attach controller to root so UI can safely delete an import as a unit.
    // (This is intentionally soft-typed to avoid leaking runtime concerns into Element's core type.)
    (root as any).vehicleMotionController = controller;
    (root as any).vehicleMotionAnimatedHelpers = { body, wheels };

    // Make sure first frame is applied immediately.
    // (Timeline.setFrame will fire timeline.frame but our controller will also catch the current frame on subsequent updates.)
    scene.forceRender = true;

    return {
        root,
        body,
        wheels,
        controller,
        warnings,
        durationSec,
        framesRequired,
        fps
    };
}
